import { useId, useRef, useState } from 'react';
import type { Moment, PieceData } from '../../data/types';
import { pathById } from '../../data';
import { figuredBass } from '../../music/figures';
import { keyName, parseKey, sameKey } from '../../music/key';
import { barAt, barEnd, barStart, Measure } from '../../music/piece';
import { parsePitch, pcName } from '../../music/pitch';
import { formatRoman } from '../../music/roman';
import type { TensionPoint } from '../../music/tension';
import { PUNCTUATION, REGION_WORDS } from '../copy';
import type { Depth, Lens } from '../hooks';
import { PieceView, bassContour, mapTime } from '../model';

interface Props {
  piece: PieceData;
  view: PieceView;
  alt: PieceView | null;
  width: number;
  position: number;
  fog: boolean;
  depth: Depth;
  lens: Lens;
  moment: Moment | null;
  loop: { start: number; end: number } | null;
  alteredBars: number[];
  onSeek: (t: number) => void;
  onLoop: (range: { start: number; end: number } | null) => void;
}

const PAD = 12;

/** Catmull-Rom spline through points, as an SVG path. */
function smooth(points: Array<[number, number]>): string {
  if (points.length < 2) return '';
  let d = `M${points[0][0].toFixed(1)},${points[0][1].toFixed(1)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)}`;
  }
  return d;
}

function valueAt(points: TensionPoint[], t: number): number {
  if (!points.length) return 0;
  if (t <= points[0].t) return points[0].v;
  for (let i = 1; i < points.length; i++) {
    if (t <= points[i].t) {
      const a = points[i - 1];
      const b = points[i];
      const f = b.t === a.t ? 0 : (t - a.t) / (b.t - a.t);
      const e = f * f * (3 - 2 * f);
      return a.v + (b.v - a.v) * e;
    }
  }
  return points[points.length - 1].v;
}

const textWidth = (s: string, px = 6.2) => s.length * px;

/** A short name for a form section, for narrow screens. */
function shortSection(label: string): string {
  const l = label.toLowerCase();
  const v = /variation\s*(\d+)/.exec(l);
  if (v) return `var. ${v[1]}`;
  if (l.startsWith('recapitulation')) return 'recap';
  if (l.startsWith('exposition') || l.startsWith('first theme')) return '1st';
  if (l.startsWith('second theme')) return '2nd';
  if (l.startsWith('development')) return 'dev.';
  if (l.startsWith('transition')) return 'tr.';
  if (l.startsWith('closing')) return 'close';
  if (l.startsWith('false')) return 'false';
  return l.slice(0, 5);
}

export function GravityScore(props: Props) {
  const { piece, view, alt, width, position, fog, depth, lens, moment, loop, alteredBars, onSeek, onLoop } = props;
  const uid = useId().replace(/:/g, '');
  const measures: Measure[] = view.core.measures;
  const length = view.length;
  const innerW = Math.max(10, width - PAD * 2);
  const x = (t: number) => PAD + (t / length) * innerW;
  const tAt = (px: number) => Math.max(0, Math.min(length, ((px - PAD) / innerW) * length));
  const pxPerBar = innerW / measures.length;
  const narrow = width < 560;

  const breathH = narrow ? 128 : 170;
  const top = 10;
  const yv = (v: number) => top + (1 - v) * breathH * 0.9 + breathH * 0.05;
  let cursorY = top + breathH + 6;
  const showMap = !fog;
  const sections = piece.sections ?? [];
  const rowForm = showMap && sections.length ? { y: cursorY, h: 22 } : null;
  if (rowForm) cursorY += rowForm.h + 4;
  const rowRegions = showMap ? { y: cursorY, h: 22 } : null;
  if (rowRegions) cursorY += rowRegions.h + 6;
  const rowPunct = showMap ? { y: cursorY, h: depth >= 1 ? 38 : 28 } : null;
  if (rowPunct) cursorY += rowPunct.h + 2;
  const rowScript = showMap ? { y: cursorY, h: 34 } : null;
  if (rowScript) cursorY += rowScript.h + 6;
  const pxPerEvent = innerW / Math.max(1, view.events.length);
  const stagger = pxPerEvent < 52;
  const rowSymbols = showMap && depth >= 3 && pxPerEvent >= 22 ? { y: cursorY, h: stagger ? 32 : 20 } : null;
  if (rowSymbols) cursorY += rowSymbols.h + 4;
  const rowBars = { y: cursorY, h: 16 };
  const height = cursorY + rowBars.h + 4;

  // Curves
  const pts = view.tension.points;
  const curve = smooth(pts.map((p) => [x(p.t), yv(p.v)]));
  const area = curve ? `${curve} L${x(pts[pts.length - 1].t).toFixed(1)},${(top + breathH).toFixed(1)} L${x(pts[0].t).toFixed(1)},${(top + breathH).toFixed(1)} Z` : '';

  const altSegments: string[] = [];
  if (alt) {
    let seg: Array<[number, number]> = [];
    let prevT = -1;
    let prevAltT = -1;
    for (const p of alt.tension.points) {
      const t = mapTime(alt.core.measures, measures, p.t);
      if (seg.length && t - prevT > p.t - prevAltT + 1.5) {
        altSegments.push(smooth(seg));
        seg = [];
      }
      seg.push([x(t), yv(p.v)]);
      prevT = t;
      prevAltT = p.t;
    }
    if (seg.length > 1) altSegments.push(smooth(seg));
  }

  // Pointer: click to seek, drag to loop whole bars
  const drag = useRef<{ x0: number; active: boolean } | null>(null);
  const [draft, setDraft] = useState<{ start: number; end: number } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const localX = (e: React.PointerEvent) => {
    const r = svgRef.current!.getBoundingClientRect();
    return ((e.clientX - r.left) / r.width) * width;
  };
  const barRange = (a: number, b: number) => {
    const t1 = tAt(Math.min(a, b));
    const t2 = tAt(Math.max(a, b));
    return { start: barStart(measures, barAt(measures, t1)), end: barEnd(measures, barAt(measures, t2)) };
  };
  const onDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    drag.current = { x0: localX(e), active: false };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const xx = localX(e);
    if (!d.active && Math.abs(xx - d.x0) > 8) d.active = true;
    if (d.active) setDraft(barRange(d.x0, xx));
  };
  const onUp = (e: React.PointerEvent) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (d.active) {
      onLoop(barRange(d.x0, localX(e)));
      setDraft(null);
    } else {
      onSeek(tAt(localX(e)));
    }
  };

  const shownLoop = draft ?? loop;
  const headV = valueAt(pts, position);

  // Punctuation labels, greedily placed on two lines
  const punctItems: Array<{ x: number; lx: number; glyph: string; tense: boolean; label: string; line: number; theory: string }> = [];
  if (rowPunct) {
    const lineEnds = [-Infinity, -Infinity];
    const right = width - PAD;
    for (const p of view.punctuation) {
      if (!measures.some((m) => m.bar === p.bar)) continue;
      const px = x(p.at ?? barStart(measures, p.bar));
      const info = PUNCTUATION[p.mark];
      const label = depth === 0 ? '' : depth === 1 ? p.label : depth === 2 ? info.name : info.abbr;
      const w = textWidth(label, 6.4);
      let line = -1;
      let lx = Math.min(px + 12, right - w);
      if (label && lx >= px - 4) {
        for (const l of [0, 1]) {
          if (lx > lineEnds[l]) {
            line = l;
            break;
          }
        }
      }
      if (line < 0) lx = px + 12;
      else lineEnds[line] = lx + w + 10;
      punctItems.push({ x: px, lx, glyph: info.glyph, tense: info.tense, label: line >= 0 ? label : '', line: Math.max(0, line), theory: p.theory });
    }
  }

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={`Tension of ${piece.title} over time${fog ? ', revealed as it plays' : ''}. Click to move the playhead; drag across bars to loop them.`}
    >
      <defs>
        <linearGradient id={`tg${uid}`} gradientUnits="userSpaceOnUse" x1="0" y1={top + breathH} x2="0" y2={top}>
          <stop offset="0" className="st-rest" />
          <stop offset="1" className="st-tension" />
        </linearGradient>
        <linearGradient id={`tw${uid}`} gradientUnits="userSpaceOnUse" x1="0" y1={top} x2="0" y2={top + breathH}>
          <stop offset="0" className="st-tension" stopOpacity={0.2} />
          <stop offset="1" className="st-rest" stopOpacity={0.02} />
        </linearGradient>
        <linearGradient id={`fog${uid}`} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" className="st-surface" stopOpacity={0} />
          <stop offset="0.35" className="st-surface" stopOpacity={0.85} />
          <stop offset="1" className="st-surface" stopOpacity={1} />
        </linearGradient>
        <clipPath id={`played${uid}`}>
          <rect x={0} y={0} width={fog ? Math.max(0, x(position)) : width} height={height} />
        </clipPath>
      </defs>

      {/* Interaction surface */}
      <rect
        x={0}
        y={0}
        width={width}
        height={top + breathH}
        fill="transparent"
        style={{ cursor: 'crosshair' }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
      />

      {/* Moment band and loop */}
      {showMap && moment && measures.some((m) => m.bar === moment.from) && (
        <rect
          x={x(moment.start ?? barStart(measures, moment.from))}
          y={top}
          width={x(moment.end ?? barEnd(measures, Math.min(moment.to, measures[measures.length - 1].bar))) - x(moment.start ?? barStart(measures, moment.from))}
          height={breathH}
          fill="var(--rest-wash)"
          pointerEvents="none"
        />
      )}
      {shownLoop && (
        <g pointerEvents="none">
          <rect x={x(shownLoop.start)} y={top} width={x(shownLoop.end) - x(shownLoop.start)} height={breathH} fill="var(--tension-wash)" />
          <line x1={x(shownLoop.start)} x2={x(shownLoop.end)} y1={top + 1} y2={top + 1} className="s-tension" strokeWidth={2} />
        </g>
      )}

      {/* Bar grid */}
      <g pointerEvents="none">
        {measures.map((m) => (
          <line key={m.bar} x1={x(m.start)} x2={x(m.start)} y1={top} y2={top + breathH} className="s-grid" strokeOpacity={fog ? 0.5 : 1} />
        ))}
        <line x1={PAD} x2={width - PAD} y1={top + breathH} y2={top + breathH} className="s-rule" />
      </g>

      {/* The breath */}
      <g clipPath={`url(#played${uid})`} pointerEvents="none">
        {area && <path d={area} fill={`url(#tw${uid})`} />}
        {curve && <path d={curve} fill="none" stroke={`url(#tg${uid})`} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />}
      </g>
      {altSegments.map((d, i) => (
        <path key={i} d={d} fill="none" className="s-ink" strokeWidth={1.8} strokeDasharray="4 5" strokeOpacity={0.75} pointerEvents="none" />
      ))}
      {fog && (
        <g pointerEvents="none">
          <rect x={x(position)} y={top} width={Math.max(0, width - x(position))} height={breathH} fill={`url(#fog${uid})`} />
          <circle cx={x(position)} cy={yv(headV)} r={position > 0 ? 6 : 0} fill={`url(#tg${uid})`} />
          <circle cx={x(position)} cy={yv(headV)} r={position > 0 ? 12 : 0} fill="var(--tension)" opacity={0.12 + headV * 0.2} />
        </g>
      )}

      {/* Form: the sections the analysis names */}
      {rowForm && (
        <g pointerEvents="none">
          {sections.map((sec, i) => {
            const x1 = x(sec.start);
            const x2 = x(sec.end);
            const m = piece.moments.find((mm) => mm.id === `section-${i + 1}`);
            const full = depth === 0 ? '' : depth === 1 ? (m?.feeling ?? sec.label) : sec.label;
            const label = full && x2 - x1 > textWidth(full, 6) + 10 ? full : shortSection(sec.label);
            return (
              <g key={`${sec.start}-${sec.label}`}>
                <title>{sec.label}</title>
                <rect x={x1 + 0.5} y={rowForm.y} width={Math.max(0, x2 - x1 - 1)} height={rowForm.h} rx={3} className={i % 2 ? 'form-b' : 'form-a'} />
                {label && x2 - x1 > textWidth(label, 6) + 10 && (
                  <text x={x1 + 6} y={rowForm.y + 15} className="t-small">
                    {label}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      )}

      {/* Ground: key regions and pedals */}
      {rowRegions && (
        <g pointerEvents="none">
          {view.regions.map((r) => {
            if (!measures.some((m) => m.bar >= r.from && m.bar <= r.to)) return null;
            const fromBar = measures.find((m) => m.bar >= r.from)!.bar;
            const toBar = [...measures].reverse().find((m) => m.bar <= r.to)!.bar;
            const x1 = x(r.start ?? barStart(measures, fromBar));
            const x2 = x(r.end ?? barEnd(measures, toBar));
            const label = depth === 0 ? '' : depth === 1 ? REGION_WORDS[r.role] : keyName(parseKey(r.key));
            return (
              <g key={`${r.from}-${r.key}`}>
                <rect x={x1 + 0.5} y={rowRegions.y} width={Math.max(0, x2 - x1 - 1)} height={rowRegions.h} className={`region-${r.role}`} rx={3} />
                {label && x2 - x1 > textWidth(label, 6) + 10 && (
                  <text x={x1 + 6} y={rowRegions.y + 15} className="t-small">
                    {label}
                  </text>
                )}
              </g>
            );
          })}
          {view.core.pedals.map((p) => {
            if (!measures.some((m) => m.bar === p.from)) return null;
            const x1 = x(barStart(measures, p.from));
            const x2 = x(barEnd(measures, p.to));
            const label = depth === 0 ? '' : depth === 1 ? `held ${pcName(parsePitch(p.pitch))}` : `${p.kind} pedal`;
            const tx = x1 + 6;
            return (
              <g key={`pedal-${p.from}`}>
                <line x1={x1 + 2} x2={x2 - 2} y1={rowRegions.y + rowRegions.h - 2} y2={rowRegions.y + rowRegions.h - 2} className={p.kind === 'dominant' ? 's-tension' : 's-rest'} strokeWidth={3} strokeLinecap="round" />
                {label && x2 - x1 > textWidth(label, 6) + 10 && (
                  <text x={tx} y={rowRegions.y + 13} className="t-small" style={{ fill: 'var(--ink-2)' }}>
                    {label}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      )}

      {/* Punctuation */}
      {rowPunct && (
        <g pointerEvents="none">
          {punctItems.map((p, i) => (
            <g key={i}>
              <title>{p.theory}</title>
              <text x={p.x + 1} y={rowPunct.y + 20} className={`t-punct ${p.tense ? 'f-tension' : 'f-rest'}`}>
                {p.glyph}
              </text>
              {p.label && (
                <text x={p.lx} y={rowPunct.y + 14 + p.line * 13} className="t-italic" style={{ fontSize: 12 }}>
                  {p.label}
                </text>
              )}
            </g>
          ))}
        </g>
      )}

      {/* The script of paths */}
      {rowScript && (
        <g>
          {piece.script.map((s) => {
            if (!measures.some((m) => m.bar === s.from)) return null;
            const path = pathById(s.path);
            if (!path) return null;
            const toBar = [...measures].reverse().find((m) => m.bar <= s.to)!.bar;
            const x1 = x(s.start ?? barStart(measures, s.from)) + 1;
            const x2 = x(s.end ?? barEnd(measures, toBar)) - 1;
            const w = x2 - x1;
            const contour = bassContour(path.prototype, path.key);
            const gw = Math.min(36, w - 8);
            const steps = contour.map((c) => c.step);
            const lo = Math.min(0, ...steps);
            const hi = Math.max(1, ...steps);
            const gx = (i: number) => x1 + 5 + (contour.length > 1 ? (i * gw) / (contour.length - 1) : gw / 2);
            const gy = (st: number) => rowScript.y + 6 + ((hi - st) / (hi - lo || 1)) * (rowScript.h - 12);
            const label = depth === 0 ? '' : depth === 1 ? path.feeling : path.name;
            const fitsBeside = !!label && w - gw - 16 > textWidth(label, 6.3);
            const fitsAlone = !!label && !fitsBeside && w - 12 > textWidth(label, 6.3);
            const showGlyph = !fitsAlone;
            return (
              <a key={`${s.from}-${s.path}`} href={`#path.${path.id}`} className="script-seg-link" aria-label={`${path.name}: ${path.feeling}`}>
                <title>{`${path.name}: ${path.feeling}`}</title>
                <rect x={x1} y={rowScript.y} width={Math.max(0, w)} height={rowScript.h} rx={6} className="script-seg" />
                {showGlyph && gw > 10 && contour.length > 1 && (
                  <polyline points={contour.map((c, i) => `${gx(i)},${gy(c.step)}`).join(' ')} fill="none" stroke="var(--ink-2)" strokeWidth={1.6} strokeLinejoin="round" />
                )}
                {(fitsBeside || fitsAlone) && (
                  <text x={fitsAlone ? x1 + 7 : x1 + gw + 12} y={rowScript.y + 21} className="t-label">
                    {label}
                  </text>
                )}
              </a>
            );
          })}
        </g>
      )}

      {/* Symbols (depth 3+) */}
      {rowSymbols && (
        <g pointerEvents="none">
          {view.events.map((ev, i) => {
            const prev = view.events[i - 1];
            const cx = (x(ev.start) + x(ev.end)) / 2;
            let text: string;
            if (lens === 'roman') {
              const keyChange = !prev || !sameKey(prev.key, ev.key);
              const sym = formatRoman(ev.spec.rn);
              text = keyChange && pxPerEvent > 44 ? `${ev.spec.key}: ${sym}` : sym;
            } else {
              const uniq = [...new Map(ev.sounding.map((n) => [n.midi, n])).values()].sort((a, b) => a.midi - b.midi);
              const figs = figuredBass(uniq.map((n) => parsePitch(n.name)), ev.key);
              text = figs.length ? figs.join(' ') : '–';
            }
            return (
              <text key={i} x={cx} y={rowSymbols.y + 13 + (stagger && i % 2 === 1 ? 13 : 0)} textAnchor="middle" className="t-rn" style={{ fontSize: pxPerEvent < 32 ? 11 : 12.5 }}>
                {text}
              </text>
            );
          })}
        </g>
      )}

      {/* Bar numbers and altered bars */}
      <g pointerEvents="none">
        {measures.map((m, i) => {
          const step = pxPerBar >= 20 ? 1 : pxPerBar >= 10 ? 4 : pxPerBar >= 5 ? 8 : pxPerBar >= 2.5 ? 16 : 32;
          const show = i === 0 || (m.bar - 1) % step === 0;
          const altered = alteredBars.includes(m.bar);
          return (
            <g key={m.bar}>
              {altered && <rect x={x(m.start) + 1} y={rowBars.y - 2} width={Math.max(1, x(m.start + m.length) - x(m.start) - 2)} height={2} className="f-tension" />}
              {show && (
                <text x={x(m.start) + 2} y={rowBars.y + 11} className="t-mono">
                  {m.bar}
                </text>
              )}
            </g>
          );
        })}
      </g>

      {/* Playhead */}
      {!fog && (
        <line x1={x(position)} x2={x(position)} y1={top - 4} y2={height - 2} className="s-ink" strokeWidth={1.5} pointerEvents="none" />
      )}
    </svg>
  );
}
